/** Consolidated UUID and ID generation utilities Single source of truth for all ID generation across modules */

/**
 * Generates a RFC4122 v4 UUID Used as the canonical UUID generator across all modules
 *
 * @example
 *   generateUUID(); // Returns "f47ac10b-58cc-4372-a567-0e02b2c3d479"
 *
 * @returns UUID v4 string in format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 */
export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Generates a template-specific ID with UUID Combines prefix with UUID for easy identification
 *
 * @example
 *   generateTemplateId(); // Returns "tpl-f47ac10b-58cc-4372-a567-0e02b2c3d479"
 *
 * @returns Template ID string in format: tpl-{uuid}
 */
export function generateTemplateId(): string {
  return `tpl-${generateUUID()}`;
}

/**
 * Generates a control-specific ID with UUID Combines prefix with UUID for easy identification
 *
 * @example
 *   generateControlId(); // Returns "ctrl-f47ac10b-58cc-4372-a567-0e02b2c3d479"
 *
 * @returns Control ID string in format: ctrl-{uuid}
 */
export function generateControlId(): string {
  return `ctrl-${generateUUID()}`;
}

/**
 * Generates a section-specific ID with UUID Combines prefix with UUID for easy identification
 *
 * @example
 *   generateSectionId(); // Returns "sec-f47ac10b-58cc-4372-a567-0e02b2c3d479"
 *
 * @returns Section ID string in format: sec-{uuid}
 */
export function generateSectionId(): string {
  return `sec-${generateUUID()}`;
}

/**
 * Generates a questionnaire-specific ID with UUID
 *
 * @example
 *   generateQuestionnaireId(); // Returns "qs-f47ac10b-58cc-4372-a567-0e02b2c3d479"
 *
 * @returns Questionnaire ID string in format: qs-{uuid}
 */
export function generateQuestionnaireId(): string {
  return `qs-${generateUUID()}`;
}

/**
 * Generates a random short ID for temporary use 8-character alphanumeric string
 *
 * @example
 *   generateShortId(); // Returns "a3f7k9x2"
 *
 * @returns Random short ID string
 */
export function generateShortId(): string {
  return Math.random().toString(36).substring(2, 10);
}

/**
 * Generates a numeric ID based on timestamp and random component Useful for database IDs that need numeric format
 *
 * @example
 *   generateNumericId(); // Returns something like "1708015445123456"
 *
 * @returns Numeric ID string
 */
export function generateNumericId(): number {
  return Math.floor(Date.now() * 1000 + Math.random() * 1000);
}

/**
 * Generates a prefixed ID with numeric timestamp Combines prefix, timestamp, and random component
 *
 * @example
 *   generatePrefixedId('app'); // Returns "app-1708015445123-4567"
 *
 * @param prefix - The prefix for the ID (e.g., 'app', 'tpl', 'ctrl')
 * @returns Prefixed ID string in format: {prefix}-{timestamp}-{random}
 */
export function generatePrefixedId(prefix: string): string {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 10000);
  return `${prefix}-${timestamp}-${random}`;
}
